import { useQuery, type UseQueryResult, type UseQueryOptions } from '@tanstack/react-query';
import { useState } from 'react';
import type { FixedDeathCoverState, useFixedDeathCalcResponse, DeathCoverData, useDeathCoverCalcResponse, SalaryContinuanceQuery, SalaryContinuanceItemQuery, SalaryContinuanceItemResolver, SalaryContinuanceResolver, useSalaryContinuanceData, useSalaryContinuanceCalcResponse, WaitingPeriodOptions } from './types';

export const currencyFormat = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
});

export const displayFormat = (num: number): string => {
    if (num > 0) {
        return currencyFormat.format(num);
    }
    return 'N/A';
};

function fetcher<TData>(endpoint: string) {
    return async(): Promise<TData> => {
        const response = await fetch(endpoint, { method: 'GET' });

        if (!response.ok) {
            throw new Error('error fetching calculator data');
        }

        return response.json();
    };
}

export const useCalculatorQuery = <
    TData,
    TReturn = TData,
    TError = unknown
> (
        endpoint: string,
        queryKey = `calculator-query`,
        options?: UseQueryOptions<TData, TError, TReturn>
    ): UseQueryResult<TReturn, TError> => {
    return useQuery<TData, TError, TReturn>([queryKey], fetcher<TData>(endpoint), options);
};

export const useDeathCoverCalc = (): useDeathCoverCalcResponse => {
    const [selectedData, setSelectedData] = useState<DeathCoverData>({
        age: 0,
        deathCover: 0,
        deathCoverWeeklyRate: 0,
        deathDefaultUnit: 0,
        tpdCoverWeeklyRate: 0,
        tpdDefaultUnit: 0,
        tpdUnit: 0,
    });

    const { age, deathCover: deathCoverPerUnit, deathCoverWeeklyRate, deathDefaultUnit, tpdCoverWeeklyRate, tpdDefaultUnit, tpdUnit: tpdCoverPerUnit } = selectedData;

    const totalDeathCover = deathCoverPerUnit * deathDefaultUnit;
    const totalTpdCover = tpdCoverPerUnit * tpdDefaultUnit;

    return { age, selectedData, setSelectedData, deathCoverPerUnit, totalDeathCover, deathCoverWeeklyRate, tpdCoverPerUnit, totalTpdCover, tpdCoverWeeklyRate };
};


export const useFixedDeathCalc = (): useFixedDeathCalcResponse => {
    const [data, setData] = useState<FixedDeathCoverState>({
        age: 0,
        maleDO: 0,
        maleDTPD: 0,
        femaleDO: 0,
        femaleDTPD: 0,
        gender: 'male',
        cover: 0,
    });

    const { gender, cover, maleDO, maleDTPD, femaleDO, femaleDTPD } = data;

    let deathOnlyFee = 0;
    let deathTPDFee = 0;
    const coverLevel = cover / 10000;

    switch (gender) {
    case 'female':
        deathOnlyFee = femaleDO * coverLevel;
        deathTPDFee = femaleDTPD * coverLevel;
        break;
    default:
        deathOnlyFee = maleDO * coverLevel;
        deathTPDFee = maleDTPD * coverLevel;
        break;
    }

    return { deathOnlyFee, deathTPDFee, setData };
};

function roundToNearestHundred(num: number): number {
    return Math.ceil(num/100)*100;
}

function resolveContinuanceItem(item: SalaryContinuanceItemQuery): SalaryContinuanceItemResolver {
    return {
        age: item.age,
        male: {
            fee30D: item.male30day,
            fee60D: item.male60day,
            fee90D: item.male90day,
        },
        female: {
            fee30D: item.female30day,
            fee60D: item.female60day,
            fee90D: item.female90day,
        },
    };
}

export function resolverSalaryContinuance(data: SalaryContinuanceQuery): SalaryContinuanceResolver {
    const fee2YGroup = data.salaryContinuance2year.map(item => {
        return resolveContinuanceItem(item);
    });

    const fee5YGroup = data.salaryContinuance5year.map(item => {
        return resolveContinuanceItem(item);
    });

    const fee60YGroup = data.salaryContinuance60year.map(item => {
        return resolveContinuanceItem(item);
    });

    const fee65YGroup = data.salaryContinuance65year.map(item => {
        return resolveContinuanceItem(item);
    });

    return {
        salaryContinuance2year: fee2YGroup,
        salaryContinuance5year: fee5YGroup,
        salaryContinuance60year: fee60YGroup,
        salaryContinuance65year: fee65YGroup,
    };
}

export const useSalaryContinuanceCalc = (): useSalaryContinuanceCalcResponse => {
    const defaultFee = {
        fee30D: 0,
        fee60D: 0,
        fee90D: 0,
    };

    const periods = ['2year', '5year', '60year', '65year'];

    const [defaultUnit, setDefaultUnit] = useState(0);

    const [data, setData] = useState<useSalaryContinuanceData>({
        salary: 0,
        percentageInsuredSalary: 0,
        gender: 'male',
    });

    const result: useSalaryContinuanceCalcResponse['result'] = {
        fee2yearPeriod: defaultFee,
        fee5yearPeriod: defaultFee,
        fee60yearPeriod: defaultFee,
        fee65yearPeriod: defaultFee,
    };

    const {
        salary,
        percentageInsuredSalary,
        gender,
    } = data;

    const annualBenefit = salary * percentageInsuredSalary / 100;
    const monthlyBenefit = roundToNearestHundred(annualBenefit / 12);

    const multiplier = Math.ceil(monthlyBenefit / defaultUnit);

    periods.forEach(period => {
        if (data[`fee${period}Period`]) {
            // get fee data based on selected gender
            const feeData = data[`fee${period}Period`][gender];

            // calculate each fee with the multiplier
            const fee30D = feeData.fee30D * multiplier;
            const fee60D = feeData.fee60D * multiplier;
            const fee90D = feeData.fee90D * multiplier;

            result[`fee${period}Period`] = {
                fee30D: parseFloat(fee30D.toFixed(2)),
                fee60D: parseFloat(fee60D.toFixed(2)),
                fee90D: parseFloat(fee90D.toFixed(2)),
            } as WaitingPeriodOptions;
        }
    });

    return { setData, setDefaultUnit, monthlyBenefit, annualBenefit, result };
};


